import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useState } from "react";

import { SAMPLE_PHOTOS } from "pages/VehiclePhotos/constants";
import { useStyles } from "pages/VehiclePhotos/styles";

import BottomDrawer from "components/BottomDrawer";
import Button from "components/Button";
import Flex from "components/Flex";
import { CameraIcon } from "components/Icons/Camera";
import InlineError from "components/InlineError";
import PageLayout from "components/PageLayout";
import PhotoGrid from "components/PhotoGrid";
import PhotoUpload from "components/PhotoUpload";
import { Photo, UnUploadedImage } from "components/PhotoUpload/types";
import Text from "components/Text";

import { clientConfigAtom } from "atoms/clientConfig";
import { pageDrawerAtom } from "atoms/pageDrawer";
import { has4PhotosUploadedAtom, vehiclePhotosAtom } from "atoms/vehiclePhotos";
import { useCheckpoint } from "hooks/useCheckpoint";
import { useClientTranslation } from "hooks/useClientTranslation";
import { useDigitalJourneyPageRouting } from "hooks/useDigitalJourneyPageRouting";
import { Checkpoint } from "types/Checkpoint";
import { isSelectedImageDuplicate } from "utils/imageProcessing/handleDuplicates";
import { processImageForUpload } from "utils/imageProcessing/processImageForUpload";

export const DJVehiclePhotosPage = () => {
  const targetReached = useCheckpoint(Checkpoint.CLAIM_SUBMITTED);
  const { onTrackAndNext, continueCTA } = useDigitalJourneyPageRouting();
  const toggleDrawer = useSetAtom(pageDrawerAtom);
  const [vehiclePhotos, setVehiclePhotos] = useAtom(vehiclePhotosAtom);
  const has4PhotosUploaded = useAtomValue(has4PhotosUploadedAtom);
  const [hasDuplicates, setHasDuplicates] = useState(false);

  const { classes } = useStyles();

  const { t } = useClientTranslation();

  const { detectDuplicatePicture } = useAtomValue(clientConfigAtom);

  const onDrop = (files: File[]) => {
    const photosToUpload = 4 - vehiclePhotos.length;
    setHasDuplicates(false);
    const toUpload: UnUploadedImage[] = [...vehiclePhotos];
    files.forEach(async (file, index) => {
      if (index >= photosToUpload) return;

      const image = await processImageForUpload({
        file,
        alt: `Vehicle photo to upload number ${vehiclePhotos.length + index + 1}`,
        type: Photo.Vehicle,
      });
      if (detectDuplicatePicture && isSelectedImageDuplicate(image, toUpload)) {
        setHasDuplicates(true);
      } else {
        toUpload.push(image);
        setVehiclePhotos((photos) => [...photos, image]);
      }
    });
  };

  return (
    <PageLayout
      title={t("vehicle_corner_photos.header")}
      dataTestId="djVehiclePhotos"
      forceContinue={targetReached}
      bottomButtons={
        <Flex gap={16}>
          {has4PhotosUploaded ? (
            <Button
              onClick={onTrackAndNext({
                name: "Digital Journey Vehicle corner photos",
                properties: { numberOfImages: vehiclePhotos.length },
              })}
            >
              {continueCTA}
            </Button>
          ) : (
            <PhotoUpload onDrop={onDrop}>
              <Button leftIcon={<CameraIcon />}>{t("global.add_photos")}</Button>
            </PhotoUpload>
          )}
        </Flex>
      }
    >
      <Flex mx={24} my={8} gap={16}>
        <Text sizeVariant="md">{t("vehicle_corner_photos.instruction")}</Text>

        <Button typeVariant="link" className={classes.explanationButton} onClick={toggleDrawer}>
          {t("vehicle_corner_photos.why.button")}
        </Button>

        {Boolean(vehiclePhotos.length) && (
          <Flex direction="row" justify="space-between" className={classes.samplePhotosRow}>
            {SAMPLE_PHOTOS.map(({ component: Image, alt }) => (
              <div className={classes.samplePhotoContainer} key={alt}>
                <Image alt={alt} className={classes.samplePhoto} />
              </div>
            ))}
          </Flex>
        )}

        {Boolean(vehiclePhotos.length) ? (
          <PhotoGrid images={vehiclePhotos} deleteFromAtom={setVehiclePhotos} />
        ) : (
          <PhotoGrid images={SAMPLE_PHOTOS} />
        )}

        {hasDuplicates && <InlineError variant="vehiclePhotos" />}
      </Flex>
      <BottomDrawer heading={t("vehicle_corner_photos.why.header")}>
        <Text sizeVariant="md">{t("vehicle_corner_photos.why.explanation")}</Text>
      </BottomDrawer>
    </PageLayout>
  );
};
